import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DateTime } from 'luxon';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';

export interface OpenConflictCrewMember {
    user_id: number;
    name: string;
}

export interface OpenConflictItem {
    allocation_id: number;
    /** Pessoa que está sendo realocada e já está nessa allocation aberta. */
    userId: number;
    userName: string;
    label: string;
    started_at: string | Date;
    /** Resto da tripulação da allocation aberta (exclui a própria pessoa). */
    crew: OpenConflictMember[];
}

export interface OpenConflictMember {
    user_id: number;
    name: string;
}

export interface CloseCurrentAllocationsData {
    timezone: string;
    conflicts: OpenConflictItem[];
}

export interface CloseCurrentAllocationsSelection {
    allocation_id: number;
    /** Pessoas dessa allocation a remover (fechar) — pode ser um subconjunto da tripulação. */
    userIds: number[];
    ended_at: string;
}

export type CloseCurrentAllocationsResult =
    | { mode: 'cancel' }
    | { mode: 'confirm'; closures: CloseCurrentAllocationsSelection[] };

interface ConflictRow {
    allocation_id: number;
    label: string;
    started_at: string | Date;
    people: { user_id: number; name: string; selected: boolean; isTriggering: boolean }[];
}

@Component({
    selector: 'app-close-current-allocations-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
    ],
    templateUrl: './close-current-allocations-dialog.component.html',
    styleUrls: ['./close-current-allocations-dialog.component.scss'],
})
export class CloseCurrentAllocationsDialogComponent {
    endedAtLocal: string;
    readonly rows: ConflictRow[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: CloseCurrentAllocationsData,
        private dialogRef: MatDialogRef<CloseCurrentAllocationsDialogComponent, CloseCurrentAllocationsResult>,
    ) {
        this.endedAtLocal = DateTime.now().setZone(data.timezone).toFormat("yyyy-MM-dd'T'HH:mm");
        this.rows = this.buildRows(data.conflicts);
    }

    get hasAnySelected(): boolean {
        return this.rows.some((r) => r.people.some((p) => p.selected));
    }

    toggleAll(row: ConflictRow, checked: boolean): void {
        for (const p of row.people) p.selected = checked;
    }

    cancel(): void {
        this.dialogRef.close({ mode: 'cancel' });
    }

    /** Continua sem fechar nada. */
    skip(): void {
        this.dialogRef.close({ mode: 'confirm', closures: [] });
    }

    confirm(): void {
        if (!this.endedAtLocal) return;
        const dt = DateTime.fromISO(this.endedAtLocal, { zone: this.data.timezone });
        const ended_at = dt.toUTC().toISO()!;
        const closures: CloseCurrentAllocationsSelection[] = this.rows
            .map((row) => ({
                allocation_id: row.allocation_id,
                userIds: row.people.filter((p) => p.selected).map((p) => p.user_id),
                ended_at,
            }))
            .filter((c) => c.userIds.length > 0);
        this.dialogRef.close({ mode: 'confirm', closures });
    }

    private buildRows(conflicts: OpenConflictItem[]): ConflictRow[] {
        const byAllocation = new Map<number, ConflictRow>();
        for (const conflict of conflicts) {
            let row = byAllocation.get(conflict.allocation_id);
            if (!row) {
                row = { allocation_id: conflict.allocation_id, label: conflict.label, started_at: conflict.started_at, people: [] };
                byAllocation.set(conflict.allocation_id, row);
            }
            if (!row.people.some((p) => p.user_id === conflict.userId)) {
                row.people.push({ user_id: conflict.userId, name: conflict.userName, selected: true, isTriggering: true });
            }
            for (const member of conflict.crew) {
                if (row.people.some((p) => p.user_id === member.user_id)) continue;
                row.people.push({ user_id: member.user_id, name: member.name, selected: false, isTriggering: false });
            }
        }
        return [...byAllocation.values()];
    }
}
